import React, { useMemo } from 'react';
import { Box, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import { BuilderDto } from '@vatos-pas/common';

import { FormToolbar, SnackbarContainer } from 'components/standalone';
import { createValidationResolver } from 'utils';
import { BuilderFormValues } from '../containers';
import { validateBuilder } from '../views/buildersValidation';

export interface BuilderFormProps {
  mode: 'create' | 'edit' | 'show';
  initialValues?: Partial<BuilderDto>;
  onSubmit?: (values: BuilderFormValues) => Promise<void>;
  loading: boolean;
  onGoBack: () => void;
}

const getDefaultValues = (
  initialValues?: Partial<BuilderDto>,
): BuilderFormValues => ({
  name: initialValues?.name || '',
  code: initialValues?.code || '',
});

export const BuilderForm: React.FC<BuilderFormProps> = ({
  mode,
  initialValues,
  onSubmit,
  loading,
  onGoBack,
}) => {
  const classes = useStyles();
  const isReadOnly = mode === 'show';
  const { notify } = SnackbarContainer.useContainer();

  const formValues = useMemo(
    () => (initialValues ? getDefaultValues(initialValues) : undefined),
    [initialValues],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BuilderFormValues>({
    defaultValues: getDefaultValues(initialValues),
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    resolver: createValidationResolver<BuilderFormValues>(validateBuilder),
    mode: 'onBlur',
  });

  const onFormSubmit = useMemo(
    () =>
      handleSubmit(
        async data => {
          if (!onSubmit) return;
          await onSubmit(data);
        },
        () => {
          notify('The form is not valid. Please check for errors', 'error');
        },
      ),
    [handleSubmit, onSubmit, notify],
  );

  return (
    <Box>
      <Box className={classes.fields}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id={field.name}
              className={classes.input}
              label="Name"
              fullWidth
              variant="filled"
              margin="dense"
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={isReadOnly}
            />
          )}
        />
        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id={field.name}
              className={classes.input}
              label="ID Code"
              fullWidth
              variant="filled"
              margin="dense"
              error={!!errors.code}
              helperText={errors.code?.message}
              disabled={isReadOnly}
            />
          )}
        />
      </Box>

      <FormToolbar
        onGoBack={onGoBack}
        onSave={onFormSubmit}
        saveLoading={loading}
        showSave={!isReadOnly}
      />
    </Box>
  );
};

const useStyles = makeStyles({
  fields: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 0',
  },
  input: {
    margin: '0px 15px',
  },
});

export default BuilderForm;
